/**
 * The last URL (path and query) this tab showed for each page, per project, so a breadcrumb or
 * Back link to a list returns to it as the person left it: its filters, search and page.
 *
 * Kept in memory only: it lasts while the tab runs the app. Each page notes the project it's in.
 * After a reload a plain link to the list still brings back its filters, through the list's
 * remembered view (`useUrlState`'s `rememberAs`), but not its page.
 */
const MAX_PAGES = 100;
const lastUrls = new Map<string, string>();
/** The scope (project, or user) of the page last noted. */
let currentScope: string | null | undefined;

function scopedKey(scopeId: string | null | undefined, pathname: string): string {
  return `${scopeId ?? ''}|${pathname}`;
}

/** Note the URL a page is showing now. PageLayout calls this on every location change. */
export function rememberPageUrl(pathname: string, search: string, scopeId: string | null | undefined): void {
  currentScope = scopeId;
  const key = scopedKey(scopeId, pathname);
  lastUrls.delete(key);
  lastUrls.set(key, search && search !== '?' ? `${pathname}${search}` : pathname);
  if (lastUrls.size > MAX_PAGES) {
    const oldest = lastUrls.keys().next().value;
    if (oldest !== undefined) lastUrls.delete(oldest);
  }
}

/**
 * Where a link back to `path` should go: the URL this tab last showed for it (with its query),
 * or `path` itself when it hasn't been open.
 */
export function listReturnHref(path: string, scopeId: string | null | undefined = currentScope): string {
  return lastUrls.get(scopedKey(scopeId, path)) ?? path;
}

/** For tests. */
export function forgetPageUrls(): void {
  lastUrls.clear();
  currentScope = undefined;
}
